import { fetchPartnersStatistics } from '../../api';
import { calculatePartnersChartOptions } from './partners-chart-options';
import { chartConfig } from '@/features/order-statistics/config';
import { Env } from '@/shared/config/environment';

const state = {
  partnersStatistics: null,
  isLoadingPartners: false,
  partnersError: null,
  activeFilters: {
    product: null,
    period: [],
    periodMode: null,
  },

  partnersAmountChartOptions: null,
  partnersEarningsChartOptions: null,
  partnersCountChartOptions: null,
};

const mutations = {
  SET_PARTNERS_STATISTICS(state, stats) {
    state.partnersStatistics = stats;
  },
  SET_LOADING_PARTNERS(state, isLoading) {
    state.isLoadingPartners = isLoading;
  },
  SET_PARTNERS_ERROR(state, error) {
    state.partnersError = error;
  },
  SET_PARTNERS_AMOUNT_CHART_OPTIONS(state, options) {
    state.partnersAmountChartOptions = options;
  },
  SET_PARTNERS_EARNINGS_CHART_OPTIONS(state, options) {
    state.partnersEarningsChartOptions = options;
  },
  SET_PARTNERS_COUNT_CHART_OPTIONS(state, options) {
    state.partnersCountChartOptions = options;
  },
  SET_ACTIVE_FILTERS(state, filter) {
    state.activeFilters = filter;
  },
  SET_ACTIVE_FILTERS_PERIOD_MODE(state, mode) {
    state.activeFilters.periodMode = mode;
  },
};

const actions = {
  clearPartnersData({ commit }) {
    commit('SET_PARTNERS_STATISTICS', null);
    commit('SET_PARTNERS_AMOUNT_CHART_OPTIONS', null);
    commit('SET_PARTNERS_COUNT_CHART_OPTIONS', null);
    commit('SET_PARTNERS_EARNINGS_CHART_OPTIONS', null);
  },
  async fetchPartnersStatistics({ commit, dispatch, state }) {
    commit('SET_LOADING_PARTNERS', true);
    commit('SET_PARTNERS_ERROR', null);
    dispatch('clearPartnersData');

    try {
      const stats = await fetchPartnersStatistics(state.activeFilters);
      commit('SET_PARTNERS_STATISTICS', stats);
      dispatch('calculatePartnersChartOptions');

      return stats;
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося отримати статистику партнерів';

      commit('SET_PARTNERS_ERROR', errorMessage);
      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося отримати статистику партнерів',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    } finally {
      commit('SET_LOADING_PARTNERS', false);
    }
  },
  calculatePartnersChartOptions: ({ state, commit }) => {
    if (state.partnersStatistics) {
      const chartAmountOptions = calculatePartnersChartOptions(state.partnersStatistics, 'total_amount');
      commit('SET_PARTNERS_AMOUNT_CHART_OPTIONS', chartAmountOptions);

      const chartEarningsOptions = calculatePartnersChartOptions(state.partnersStatistics, 'total_earnings');
      commit('SET_PARTNERS_EARNINGS_CHART_OPTIONS', chartEarningsOptions);

      const chartCountOptions = calculatePartnersChartOptions(state.partnersStatistics, 'order_count');
      commit('SET_PARTNERS_COUNT_CHART_OPTIONS', chartCountOptions);
    }
  },
  setupFilter: ({ state, commit, dispatch }, { filters, mode = 'replace', refetchData = true }) => {
    if (mode === 'replace') {
      commit('SET_ACTIVE_FILTERS', filters);
    } else {
      commit('SET_ACTIVE_FILTERS', { ...state.activeFilters, ...filters });
    }

    if (refetchData) {
      dispatch('fetchPartnersStatistics');
    }
  },
  setupPeriodMode: ({ commit }, mode) => {
    commit('SET_ACTIVE_FILTERS_PERIOD_MODE', mode);
  },
};

const getters = {
  partnersStatistics: (state) => state.partnersStatistics,
  isLoadingPartners: (state) => state.isLoadingPartners,
  partnersError: (state) => state.partnersError,

  isPartnersDataReady: (state) => { return state.isLoadingPartners === false && state.partnersStatistics !== null &&
    state.partnersAmountChartOptions !== null && state.partnersEarningsChartOptions !== null
    && state.partnersCountChartOptions !== null; },
  isChartEmpty: (state) => state.partnersStatistics?.length === 0,

  getStatisticChartOptions: (state) => (type) => { return state[`partners${type.charAt(0).toUpperCase() + type.slice(1)}ChartOptions`]; },
  getEmptyOrdersChartMessage: () => chartConfig.emptyMessages['message-3'].title,
  getActiveFilters: (state) => state.activeFilters,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
